import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

export const NAV_LINKS = [
  { label: "Expertises", href: "#expertises" },
  { label: "Réalisations", href: "#realisations" },
  { label: "Méthode", href: "#methode" },
  { label: "Professionnels", href: "#professionnels" },
  { label: "À propos", href: "#a-propos" },
  { label: "Contact", href: "#contact" },
];

export const Logo = ({ light = false, testId = "logo", className = "h-11" }) => (
  <img
    src={light ? "/images/logo-white.png" : "/images/logo.png"}
    alt="LIMEN — Aménagement & Rénovation"
    data-testid={testId}
    className={`${className} w-auto select-none`}
  />
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (document.querySelector(href)) {
      scrollToId(href);
    } else {
      navigate("/");
      setTimeout(() => scrollToId(href), 600);
    }
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-40 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "bg-casse/90 backdrop-blur-md border-b border-mineral" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between gap-8">
          <button onClick={() => go("#top")} className="shrink-0 text-left" aria-label="LIMEN — retour en haut">
            <Logo testId="header-logo" />
          </button>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" aria-label="Navigation principale" data-testid="main-nav">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.href.slice(1)}`}
                onClick={(e) => { e.preventDefault(); go(l.href); }}
                className="relative whitespace-nowrap font-accent text-xs font-medium tracking-[0.12em] uppercase text-anthracite/80 hover:text-nuit transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-sable-deep after:transition-[width] after:duration-300 hover:after:w-full"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              data-testid="header-cta-button"
              onClick={() => go("#contact")}
              className="hidden sm:inline-flex items-center whitespace-nowrap bg-nuit px-6 py-3 font-accent text-xs font-medium tracking-[0.12em] uppercase text-casse hover:bg-anthracite transition-colors duration-300"
            >
              Demander une étude
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              className="lg:hidden p-2 text-nuit"
              aria-label="Ouvrir le menu"
            >
              <Menu size={26} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-50 bg-nuit flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="h-20 px-6 flex items-center justify-between">
              <Logo light testId="mobile-menu-logo" />
              <button onClick={() => setOpen(false)} className="p-2 text-casse" aria-label="Fermer le menu" data-testid="mobile-menu-close">
                <X size={28} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Navigation mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-${l.href.slice(1)}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                  onClick={(e) => { e.preventDefault(); go(l.href); }}
                  className="text-left py-3 border-b border-casse/10 font-heading text-2xl font-semibold text-casse hover:text-sable transition-colors duration-300"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <div className="p-8">
              <button
                data-testid="mobile-cta-button"
                onClick={() => go("#contact")}
                className="w-full bg-sable px-6 py-4 font-accent text-sm font-medium tracking-[0.12em] uppercase text-nuit"
              >
                Demander une étude
              </button>
              <a href="tel:+33786256965" data-testid="mobile-phone-link" className="mt-6 block text-center font-accent text-sm tracking-[0.2em] text-casse/80">
                07 86 25 69 65
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
